// show_choices on the stage (spec-v4 section 8.3): up to four options side by side, lettered A-D, each with its own
// Pick button. A tap does what the tool would (a voice is set, a frame approved) where the app has a route for it,
// and always tells the Director "[app] The user picked B by tapping", so voice and touch stay in step. V4-K1: that
// line is an [app] user message (the Director answers and moves on), sent once it isn't speaking.
import { approveArtifact, useVoice } from "../actions";
import { toggleAudio } from "../audio";
import { Icon } from "../components/bits";
import { tellDirector } from "../director";
import { isActive } from "../playback";
import { playNow } from "../player";
import { markActivity, useStore } from "../store";
import type { StageChoice, StageItem } from "../types";
import { dispatchStage } from "./actions";
import { Shot } from "./items";
import { liveComparison } from "../compare";
import { CompareView, compareAspect } from "../components/Compare";
import type { Comparison } from "../types";
import { useStageLoad } from "./data";

type Choices = Extract<StageItem, { kind: "choices" }>;

/** What the Director hears when the user taps Pick (an [app] user message, via tellDirector). */
export const pickLine = (c: StageChoice, kind: Choices["choiceKind"]) => `[app] The user picked ${c.letter} by tapping: ${c.title} (${kind}, id ${c.id}).`;

export async function pickChoice(item: Choices, c: StageChoice) {
  markActivity();
  dispatchStage({ type: "pick", letter: c.letter });
  const a = c.artifact;
  try {
    if (a?.kind === "audio" && a.voiceId) await useVoice(a);
    else if (a && (a.kind === "frame" || a.kind === "clip") && a.status === "draft") await approveArtifact(a);
  } finally {
    tellDirector(pickLine(c, item.choiceKind));
  }
}

const aspectFor = (kind: Choices["choiceKind"]) => (kind === "frames" || kind === "clips" || kind === "sheets" || kind === "looks" ? "16x9" : "1x1");

/** V4-D5: what a choice plays (a clip, a song, a voice sample), if anything. */
export function choiceMedia(c: StageChoice, kind: Choices["choiceKind"]): { url: string; what: "clip" | "audio" } | null {
  const a = c.artifact;
  if (a?.url && a.kind === "clip") return { url: a.url, what: "clip" };
  if (a?.url && (a.kind === "audio" || kind === "music" || kind === "voices")) return { url: a.url, what: "audio" };
  return null;
}

/** A tap on a choice's play button: clips go through the stage player, sounds through the audio cards' player. */
function playChoice(c: StageChoice, kind: Choices["choiceKind"]) {
  const m = choiceMedia(c, kind);
  if (!m) return;
  markActivity();
  if (m.what === "clip") playNow({ url: m.url, what: "clip", title: c.title, projectId: c.artifact?.projectId ?? "", ...(c.artifact?.sceneId ? { sceneId: c.artifact.sceneId } : {}) });
  else toggleAudio(`choice:${c.id}`, m.url);
}

/**
 * v4 (V4-MC): a model comparison on the stage. The candidates come from the project as it is now (they land one by
 * one); until it loads, the choices the Director sent (model + price, no files yet).
 */
function CompareChoices({ item }: { item: Choices & { compare: NonNullable<Choices["compare"]> } }) {
  const cmp = item.compare;
  const proj = useStageLoad(`project:${cmp.projectId}`, (src) => src.project(cmp.projectId));
  const p = proj.data;
  const live = liveComparison(p, cmp);
  const n = (p?.script?.scenes.findIndex((s) => s.id === cmp.sceneId) ?? -1) + 1 || null;
  const fallback: Comparison = {
    id: cmp.id,
    step: cmp.step,
    at: "",
    pickedModel: null,
    pickedAssetId: null,
    candidates: item.items.map((c, i) => ({ index: i, letter: c.letter, model: c.model ?? c.id, short: c.title, credits: c.credits ?? 0, jobId: null, assetId: null, url: c.url ?? null, status: c.url ? "ready" : "working", label: `${c.title} · ${c.credits ?? 0} credits` })),
  };
  return <CompareView projectId={cmp.projectId} sceneId={cmp.sceneId} sceneN={n} comparison={live ?? fallback} aspect={compareAspect(p)} />;
}

export function ChoicesView({ item }: { item: Choices }) {
  if (item.compare) return <CompareChoices item={item as Choices & { compare: NonNullable<Choices["compare"]> }} />;
  return <PlainChoices item={item} />;
}

function PlainChoices({ item }: { item: Choices }) {
  const picked = useStore((s) => s.stage.picked);
  const tapped = useStore((s) => s.playing);
  const pb = useStore((s) => s.playback);
  const n = item.items.length;
  return (
    <div className={`stg-choices stg-choices-${n > 2 ? "grid" : n}`}>
      {item.items.map((c) => {
        const url = c.url ?? (c.artifact?.kind === "clip" ? undefined : c.artifact?.url);
        const media = choiceMedia(c, item.choiceKind);
        const audio = media?.what === "audio" || (!media && (item.choiceKind === "music" || item.choiceKind === "voices"));
        // v4 (V4-D1 rewrite_script, D5 looks / variations): a choice with words and no picture shows its words
        const words = item.choiceKind === "scripts" || (!url && !media && c.artifact?.text) ? (c.artifact?.text ?? "") : null;
        const on = picked === c.letter;
        // the Director's play_and_wait ("choice B") or a tap is playing this one
        const playing = !!media && ((isActive(pb) && pb.req?.url === media.url && pb.phase === "playing") || tapped === `choice:${c.id}`);
        return (
          <figure key={c.letter} className={`stg-choice${on ? " is-picked" : picked ? " is-dim" : ""}${playing ? " is-playing" : ""}`}>
            {words !== null ? (
              <div className="stg-choice-text">
                <span className="stg-letter">{c.letter}</span>
                <p>{words || c.title}</p>
              </div>
            ) : audio ? (
              <div className="stg-choice-audio">
                <span className="stg-letter">{c.letter}</span>
                <Icon name={playing ? "wave" : "music"} size={28} />
              </div>
            ) : (
              <Shot src={url} aspect={aspectFor(item.choiceKind)} alt={c.title}>
                <span className="stg-letter">{c.letter}</span>
                {on && (
                  <span className="stg-picked" aria-hidden>
                    <Icon name="check" size={18} />
                  </span>
                )}
              </Shot>
            )}
            {/* a words choice whose title is its own first words (variations) needs no caption */}
            {!(words && words.startsWith(c.title)) && <figcaption>{c.title}</figcaption>}
            <div className="stg-choice-btns">
              {media && (
                <button data-action="stage.play" type="button" className="btn btn-small btn-quiet" onClick={() => playChoice(c, item.choiceKind)} aria-label={`${playing ? "Pause" : "Play"} ${c.letter}`}>
                  <Icon name={playing ? "pause" : "play"} size={16} />
                </button>
              )}
              <button data-action="stage.pick" type="button" className={`btn btn-small ${on ? "btn-primary" : "btn-quiet"}`} onClick={() => void pickChoice(item, c)} aria-pressed={on}>
                {on ? `Picked ${c.letter}` : `Pick ${c.letter}`}
              </button>
            </div>
          </figure>
        );
      })}
    </div>
  );
}
